import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import SlidePanel from "../../components/SlidePanel/SlidePanel";
import * as hrmsService from "../../services/hrmsService";
import { FormFields, cleanValues } from "./hrForm";
import { DetailGrid, PrimaryButton, SecondaryButton } from "./hrComponents";
import { cardStyle, iconBtnStyle, statusLabel, useLoad } from "./hrUi";

// Statutory identifiers, bank details, contacts and dependants. Identifiers and bank numbers arrive
// masked unless the caller may see them; a person's own bank change goes to approval.
const COUNTRY_FIELDS = {
  IN: [
    { key: "pan", label: "PAN", max: 10 },
    { key: "aadhaar", label: "Aadhaar", max: 12 },
    { key: "uan", label: "UAN", max: 12 },
    { key: "esic_no", label: "ESIC number", max: 17 },
    { key: "pf_no", label: "PF number", max: 30 },
    { key: "pt_state_code", label: "PT state", max: 8 },
    { key: "tax_regime", label: "Tax regime", type: "select", options: [{ label: "New", value: "NEW" }, { label: "Old", value: "OLD" }] },
  ],
  AE: [
    { key: "emirates_id", label: "Emirates ID", max: 18 },
    { key: "passport_no", label: "Passport number", max: 20 },
    { key: "visa_type", label: "Visa type", max: 40 },
    { key: "visa_sponsor", label: "Visa sponsor", max: 150 },
    { key: "labour_card_no", label: "Labour card number", max: 30 },
    { key: "mohre_person_id", label: "MOHRE person ID", max: 30 },
    { key: "wps_routing_code", label: "WPS routing code", max: 32 },
    { key: "labour_contract_type", label: "Labour contract type", max: 20 },
  ],
};
const BANK_FIELDS = [
  { key: "payment_mode", label: "Payment mode", type: "select", required: true, options: [["BANK_TRANSFER", "Bank transfer"], ["WPS", "WPS"], ["CASH", "Cash"], ["CHEQUE", "Cheque"]].map(([value, label]) => ({ value, label })) },
  { key: "bank_name", label: "Bank name", max: 100 },
  { key: "branch_name", label: "Branch", max: 100 },
  { key: "account_holder", label: "Account holder", max: 150 },
  { key: "account_no", label: "Account number", max: 34 },
  { key: "routing", label: "IFSC / IBAN", max: 34 },
];
const CONTACT_FIELDS = [
  { key: "name", label: "Name", required: true, max: 150 },
  { key: "relationship", label: "Relationship", max: 40 },
  { key: "phone", label: "Phone", type: "phone", max: 32 },
  { key: "email", label: "Email", type: "email", max: 150 },
  { key: "is_emergency", label: "Emergency contact", type: "checkbox" },
];
const DEPENDANT_FIELDS = [
  { key: "name", label: "Name", required: true, max: 150 },
  { key: "relationship", label: "Relationship", max: 40 },
  { key: "date_of_birth", label: "Date of birth", type: "date" },
  { key: "gender", label: "Gender", type: "select", options: [["MALE", "Male"], ["FEMALE", "Female"], ["OTHER", "Other"]].map(([value, label]) => ({ value, label })) },
];

function EditDrawer({ show, title, fields, initial, onHide, onSave }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    // Only the editable fields: the row's id, employee id and status are not part of the form.
    if (show) setValues(Object.fromEntries(fields.filter((f) => initial && initial[f.key] !== undefined && initial[f.key] !== null).map((f) => [f.key, initial[f.key]])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);
  const save = async () => {
    setSaving(true);
    try {
      const ok = await onSave(cleanValues(values));
      if (ok) onHide();
    } finally {
      setSaving(false);
    }
  };
  return (
    <SlidePanel
      show={show}
      onHide={() => !saving && onHide()}
      title={title}
      footer={
        <>
          <SecondaryButton onClick={onHide} disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={save} disabled={saving}>
            Save
          </PrimaryButton>
        </>
      }
    >
      <FormFields values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} fields={fields} />
    </SlidePanel>
  );
}

const done = (res, okText) => {
  if (res?.success) {
    toast.success(res.message || okText);
    return true;
  }
  toast.error(hrmsService.responseMessage(res));
  return false;
};

export default function EmployeeDetailsTab({ employeeId, profile, countryPack, reload }) {
  const { access, statutory, bank, contacts, dependants } = profile;
  const isSelf = access.is_self;
  const [edit, setEdit] = useState(null); // { kind, id?, initial }
  const changes = useLoad(() => hrmsService.listEmployeeChanges(employeeId), [employeeId, profile], { enabled: isSelf || access.can_edit });
  const statFields = COUNTRY_FIELDS[countryPack] || [];
  const canEditBank = isSelf || access.can_edit_statutory;

  const save = async (values) => {
    if (edit.kind === "statutory") return done(await hrmsService.saveStatutory(employeeId, values), "Saved") && (reload(), true);
    if (edit.kind === "bank") {
      const res = await hrmsService.requestBankChange(employeeId, values);
      const ok = done(res, "Saved");
      if (ok) reload();
      return ok;
    }
    const res = await hrmsService.saveEmployeeChild(employeeId, edit.kind, edit.id, values);
    const ok = done(res, "Saved");
    if (ok) reload();
    return ok;
  };

  const remove = async (kind, id) => {
    const res = await hrmsService.deleteEmployeeChild(employeeId, kind, id).catch((e) => ({ success: false, message: e.message }));
    if (done(res, "Removed")) reload();
  };

  const editTitle = { statutory: "Statutory details", bank: "Bank details", contacts: "Contact", dependants: "Dependant" }[edit?.kind] || "";
  const editFields = { statutory: statFields, bank: BANK_FIELDS, contacts: CONTACT_FIELDS, dependants: DEPENDANT_FIELDS }[edit?.kind] || [];
  const pendingBank = (profile.pending_changes || []).some((c) => c.kind === "BANK");

  const section = (title, action, children) => (
    <div style={{ ...cardStyle, padding: 18, marginBottom: 16 }}>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h6 className="mb-0">{title}</h6>
        {action}
      </div>
      {children}
    </div>
  );
  const editBtn = (label, onClick) => (
    <button type="button" style={{ ...iconBtnStyle, fontSize: 13, fontWeight: 600 }} onClick={onClick}>
      {label}
    </button>
  );

  return (
    <>
      {statFields.length
        ? section(
            `Statutory (${countryPack})`,
            access.can_edit_statutory ? editBtn("Edit", () => setEdit({ kind: "statutory", initial: {} })) : null,
            <DetailGrid items={statFields.map((f) => [f.label, statutory[f.key]])} columns={2} />
          )
        : null}

      {section(
        "Bank",
        canEditBank ? editBtn(bank ? "Change" : "Add", () => setEdit({ kind: "bank", initial: { payment_mode: bank?.payment_mode } })) : null,
        <>
          {bank ? (
            <DetailGrid items={[["Payment mode", statusLabel(bank.payment_mode)], ["Bank", bank.bank_name], ["Branch", bank.branch_name], ["Account holder", bank.account_holder], ["Account number", bank.account_no], ["IFSC / IBAN", bank.routing]]} />
          ) : (
            <div style={{ fontSize: 13, color: "#6B7280" }}>No bank details yet.</div>
          )}
          {pendingBank ? <div style={{ fontSize: 12, color: "#92400E", marginTop: 8 }}>A bank change is waiting for approval.</div> : null}
        </>
      )}

      {[
        ["contacts", "Emergency & other contacts", contacts, (c) => `${c.name}${c.relationship ? ` (${c.relationship})` : ""}${c.is_emergency ? " · Emergency" : ""}`, (c) => [c.phone, c.email].filter(Boolean).join(" · ")],
        ["dependants", "Dependants", dependants, (d) => `${d.name}${d.relationship ? ` (${d.relationship})` : ""}`, (d) => d.date_of_birth || ""],
      ].map(([kind, title, list, primary, secondary]) =>
        section(
          title,
          isSelf || access.can_edit ? editBtn("Add", () => setEdit({ kind, initial: {} })) : null,
          list.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6B7280" }}>None added.</div>
          ) : (
            list.map((x) => (
              <div key={x.id} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: "1px solid #F3F4F6", fontSize: 14 }}>
                <div>
                  <div>{primary(x)}</div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>{secondary(x)}</div>
                </div>
                {isSelf || access.can_edit ? (
                  <span>
                    {editBtn("Edit", () => setEdit({ kind, id: x.id, initial: x }))} {editBtn("Remove", () => remove(kind, x.id))}
                  </span>
                ) : null}
              </div>
            ))
          )
        )
      )}

      {(changes.data || []).length ? (
        <div style={{ fontSize: 12, color: "#6B7280" }}>
          Recent change requests: {changes.data.slice(0, 5).map((c) => `${statusLabel(c.kind)} (${statusLabel(c.status)})`).join(", ")}
        </div>
      ) : null}

      <EditDrawer show={!!edit} title={editTitle} fields={editFields} initial={edit?.initial} onHide={() => setEdit(null)} onSave={save} />
    </>
  );
}
